import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, Field, ErrorAlert, DropdownAnimation, IField } from "src/components";
import { Bank } from "src/api";
import { EWithdrawalStep } from "src/types";
import { WithdrawalStep } from "src/hooks";

interface IWithdrawalBankSelectionStepProps {
  banksList: Bank[];
  handleStepChange: (step: EWithdrawalStep, isBackNavigation?: boolean) => void;
  handleBankSelection: ({ bank, account }: { bank?: Bank; account?: string }) => void;
  selectedBankFromState: Bank | null;
  accountNumberFromState: string;
  withdrawalStep: WithdrawalStep;
}

const WithdrawalBankSelectionStep: React.FC<IWithdrawalBankSelectionStepProps> = ({
  banksList,
  handleStepChange,
  handleBankSelection,
  selectedBankFromState,
  accountNumberFromState,
  withdrawalStep,
}): JSX.Element => {
  const { t } = useTranslation();
  const [accountNumberTouched, setAccountNumberTouched] = useState(false);
  const { stepError, stepLoading } = withdrawalStep;

  const onBankSelect = (option: IField) => {
    handleBankSelection({ bank: banksList.find((bank) => bank.bankGroupId === option.key) });
  };

  const onAccountNumberChange = (value: string) => {
    handleBankSelection({ account: value });
  };

  const bankOptions: IField[] = useMemo(
    () =>
      banksList.map((bank) => ({
        key: bank.bankGroupId,
        value: bank.bankGroupName,
      })),
    [banksList]
  );

  const errors = {
    bankSelectionError: !selectedBankFromState ? t("WITHDRAWAL_ERRORS.PLEASE_SELECT_BANK") : undefined,
    accountNumberError: !accountNumberFromState ? t("WITHDRAWAL_ERRORS.ACCOUNT_NUMBER_REQUIRED") : undefined,
  };

  const submitDisabled = Object.values(errors).some(Boolean) || !!stepError;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitDisabled) return;
    handleStepChange(EWithdrawalStep.ConfirmWithdrawal);
  };

  return (
    <form className="withdrawal-form" onSubmit={handleSubmit}>
      <DropdownAnimation
        label={t("WITHDRAWAL_STEPS.SELECT_BANK")}
        selectedValue={selectedBankFromState?.bankGroupId}
        options={bankOptions}
        placeholder={t("WITHDRAWAL_STEPS.CHOOSE_YOUR_BANK")}
        onChange={onBankSelect}
        disabled={stepLoading}
      />

      <Field
        label={t("WITHDRAWAL_STEPS.ACCOUNT_NUMBER")}
        value={accountNumberFromState}
        onChange={onAccountNumberChange}
        placeholder={t("WITHDRAWAL_STEPS.ENTER_ACCOUNT_NUMBER")}
        disabled={stepLoading}
        error={accountNumberTouched ? errors.accountNumberError : undefined}
        onBlur={() => setAccountNumberTouched(true)}
      />

      <ErrorAlert message={stepError} />

      <div className="withdrawal-form-buttons">
        <Button
          title={t("WITHDRAWAL_STEPS.BACK")}
          className="withdrawal-button"
          type="button"
          onClick={() => handleStepChange(EWithdrawalStep.AmountInput, true)}
          disabled={stepLoading}
          secondary
        />
        <Button
          title={t("WITHDRAWAL_STEPS.NEXT_STEP")}
          className="withdrawal-button"
          type="submit"
          disabled={submitDisabled}
          pending={stepLoading}
          tooltipMessage={Object.values(errors).filter(Boolean)[0]}
        />
      </div>
    </form>
  );
};

export default WithdrawalBankSelectionStep;
